/**
 * Modelo de Dominio: DireccionPersona
 * 
 * Esta es la entidad de dominio del frontend Angular para direcciones.
 * Corresponde a DireccionPersonaResponse del backend (Spring Boot).
 * 
 * Arquitectura DDD Frontend:
 * - domain/model/DireccionPersona → Entidad de dominio (esta clase)
 * - domain/service/DireccionPersonaRepository → Puerto abstracto
 * - infrastructure/adapters/HttpDireccionPersonaRepository → Adaptador HTTP
 * 
 * @author Emerson Rodriguez - Ingeneo
 */
export interface DireccionPersona {
  // Primary key
  nmconsecutivo?: number;
  
  // Foreign keys
  cdpersona?: number;
  nmtipo_persona_direccion?: number;
  cdmunicipio?: string;
  
  // Datos de dirección
  dsdireccion?: string;
  dsbarrio?: string;
  nmestrato?: number;
  dslocalidad?: string;
  
  // Teléfonos
  dstelefono_fijo?: string;
  dstelefono_celular?: string;
  dsfax?: string;
  
  // Coordenadas geográficas (VARCHAR2(30) en BD)
  ptvectorx?: string;
  ptvectory?: string;
  
  // Estado
  snactivo?: string;
  
  // Campos calculados/lookup del backend
  dstipo_direccion?: string;
  dsmunicipio?: string;
  dsdepartamento?: string;
  
  // Auditoría
  feingresa?: Date | string;
  femodifica?: Date | string;
  febaja?: Date | string;
  dni_ingresa?: string;
  dni_modifica?: string;
}

/**
 * Request DTO para crear/actualizar direcciones
 */
export interface DireccionPersonaRequest {
  cdpersona: number;
  nmtipo_persona_direccion: number | null;
  cdmunicipio: string;
  dsdireccion: string;
  dsbarrio?: string;
  nmestrato?: number;
  dslocalidad?: string;
  dstelefono_fijo?: string;
  dstelefono_celular?: string;
  dsfax?: string;
  ptvectorx?: string;
  ptvectory?: string;
}

/**
 * Tipos de dirección disponibles
 * Se obtienen dinámicamente desde el backend (TPSC_TIPO_PERSONA_DIRECC)
 * Estos son valores de ejemplo
 */
export interface TipoDireccion {
  nmconsecutivo: number;
  dstipo_direccion: string;
  snobligatorio?: string;
}

/**
 * Constantes para tipos de dirección comunes
 */
export const TIPOS_DIRECCION = {
  PRINCIPAL: 1,
  CORRESPONDENCIA: 2,
  TRABAJO: 3,
  RESIDENCIA: 4
};

/**
 * Constantes para estratos
 */
export const ESTRATOS = [
  { value: 1, label: 'Estrato 1' },
  { value: 2, label: 'Estrato 2' },
  { value: 3, label: 'Estrato 3' },
  { value: 4, label: 'Estrato 4' },
  { value: 5, label: 'Estrato 5' },
  { value: 6, label: 'Estrato 6' }
];

/**
 * Crea un DireccionPersonaRequest vacío
 */
export function createEmptyDireccionRequest(): DireccionPersonaRequest {
  return {
    cdpersona: 0,
    nmtipo_persona_direccion: null,
    cdmunicipio: '',
    dsdireccion: '',
    dsbarrio: undefined,
    nmestrato: undefined,
    dslocalidad: undefined,
    dstelefono_fijo: undefined,
    dstelefono_celular: undefined,
    dsfax: undefined,
    ptvectorx: undefined,
    ptvectory: undefined
  };
}
